'use strict';

import { action, observable, configure, makeObservable } from "mobx";
configure({enforceActions: 'always'});
import {uniq} from 'underscore';

import moment from 'moment';

import appStore from '@uw-it-sis/lib-react/lib/AppStore';
import noticesStore from './NoticesStore';
import termStore from '@uw-it-sis/lib-react/lib/TermStore';
import {States} from '@uw-it-sis/lib-react/lib/AppConstants';
import { isEmpty } from '@uw-it-sis/lib-react/lib/Utils';
import TermUtils from '@uw-it-sis/lib-react/lib/TermUtils';
import events from '@uw-it-sis/lib-react/lib/Events';
import NowDateTimeEvents from '@uw-it-sis/lib-react/lib/NowDateTimeEvents';

/**
 * Storage for Registration dates.
 *
 * This store assumes that the notices and current/published terms stores are already loaded and ready to read.
 *
 */
class RegistrationDateStore {

    state = States.initial;

    registrationDateData = {};

    now = moment(appStore.getNow());

    constructor() {
        makeObservable(this, {
            state: observable,
            registrationDateData: observable,
            mount: action,
            _buildRegistrationDateData: action
        });

        // re-mount when a new start time is set
        events.listen(NowDateTimeEvents.setNow, (newStartTime) => this.mount())
    }

    mount() {
        this.state = States.pending;

        this._buildRegistrationDateData();

        this.state = States.done;
    }

    unmount() {
        events.remove(NowDateTimeEvents.setNow, (newStartTime) => this.mount());
    }

    /**
     * Returns a moment object bound to the given quarter, if one has been discovered,
     * for the registration date appropriate for that term.
     *
     * @param quarterName name of the quarter to look up
     * @returns a moment object set to the registration date, or undefined if not found
     */
    getRegistrationDateForTerm(quarterName) {

        if (isEmpty(quarterName)) {
            return undefined;
        }

        return this.registrationDateData[quarterName.toLowerCase()].date;

    }

    /**
     * Returns a map of registration date data bound to the given quarter, if one has been discovered,
     * for the registration date appropriate for that term.
     *
     * @param quarterName name of the quarter to look up
     * @returns a map with registration date data for the term
     */
    getRegistrationDataForTerm(quarterName) {

        if (isEmpty(quarterName)) {
            return undefined;
        }

        return this.registrationDateData[quarterName.toLowerCase()];

    }

    /**
     * Parse the notices loaded for the user to determine a registration date.
     *
     * If no other dates can be found, the Period I priority registration date for the current planable term will be populated
     *
     * @private
     */
    _buildRegistrationDateData() {
        // Get the list of notices with the category 'REG_DATE'
        let notices = noticesStore.getNotices('REG_DATE');
        let regMap = {};

        for (let notice of notices) {
            // Check the notice type to see if it is a P1 date notice, and that it contains the required attributes
            if ( notice.type === 'EstPd1RegDate' && notice.attributes.Date && notice.attributes.Quarter ) {
                // Get the date from the date attribute
                let regDate = moment(notice.attributes.Date, "MM/DD/YYYY");
                // Get the term name to use as a key in the reg date object
                let termKey = notice.attributes.Quarter.toLowerCase();
                // If the term key does not exist in the reg date object, add it
                if ( isEmpty(regMap[termKey]) ) {
                    // Add data to the reg date object
                    regMap[termKey] = {
                        date: regDate,
                        notice: notice,
                        isPeriodOne: true
                    };
                }
            }
        }


        // Get the list of relevant terms loaded by the termStore
        let publishedTerms = [];
        let storePublishedTerms = termStore.get('publishedTerms');
        if (!isEmpty(storePublishedTerms)) {
            publishedTerms = storePublishedTerms;
        }

        let currentTerm = termStore.get('currentTerm');
        let currentArray = [];
        if (!isEmpty(currentTerm)) {
            currentArray = [currentTerm];
        }

        let terms = uniq(currentArray.concat(publishedTerms), term => term.id);

        for (let term of terms) {
            // Get the general P1 reg date from the term academic calendar data
            let generalP1RegDate = TermUtils.findPeriodOnePriorityRegDate(term);
            let termKey = term.codeName.toLowerCase();

            // If regMap for term is empty then we have no estimated P1 reg date, use term general P1 reg date if its not null
            if ( isEmpty(regMap[termKey]) && generalP1RegDate !== null ) {
                // since we do not know if the date we are providing here is the user's P1 reg date, set the flag to false
                regMap[termKey] = {
                    date: generalP1RegDate,
                    notice: null,
                    isPeriodOne: false
                };
            }
        }

        this.registrationDateData = regMap;
    }

    /**
     * Find out if the current date is after the P1 reg date. Can shift the number of days from P1 date with optional param
     * @param termId - term for which to get P1 reg date from
     * @param daysShift - optional num of days to subtract from P1 reg date
     * @return {boolean}
     */
    isNowAfterPeriodOnePriorityRegDate(termId, daysShift) {
        let selectedTerm = termStore.getTermId(termId, 'publishedTerms');

        let firstRegPeriodDate = TermUtils.findPeriodOnePriorityRegDate(selectedTerm);

        if (daysShift) {
            firstRegPeriodDate = moment(firstRegPeriodDate.subtract(daysShift, 'days').format('YYYY-MM-DD'));
        }

        return this.now.isAfter(firstRegPeriodDate);
    }

    /**
     * Find out if the current date is before the end of the last add date deadline.
     * @param termId - term for which to get last add date from
     * @return {boolean}
     */
    isNowBeforeLastAddDate(termId) {
        let selectedTerm = termStore.getTermId(termId, 'publishedTerms');

        // {key: "last_add_day", dateDisplay: "April 19", date: "2020-04-19", range: false, endDate: ""}
        let lastAddDay = selectedTerm.academicCalendar.find(entry => entry.key === 'last_add_day');

        return this.now.isSameOrBefore(lastAddDay.date)
    }
}

export default new RegistrationDateStore();
